import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { questionTypeValues, surveyStatsType } from '../../constants/types';
import styles from './surveyQuestionSummary.module.css';
import { getTranslation, langType, withLang } from '../../i18n';
import { getSurveySchemaPartQuestions, isNumericQuestion } from '../../util';
import QuestionBarChart from '../QuestionBarChart';
import QuestionSummaryLegend from '../QuestionSummaryLegend';

const maxValue = (values) => Math.max(...values);

const minValue = (values) => Math.min(...values);

const getQuestionSummary = (question, questionStatistics, lang) => {
  if (!questionStatistics) {
    return null;
  }
  let summaryLegend = null;
  if (question.type === questionTypeValues.LIKERT) {
    const distributionValues = Object.keys(questionStatistics.distribution).map(Number);
    summaryLegend = (
      <QuestionSummaryLegend
        groupValue={questionStatistics.average}
        standardDeviation={questionStatistics.standardDeviation}
        maxValue={maxValue(distributionValues)}
        minValue={minValue(distributionValues)}
      />
    );
  }
  return (
    <div className={styles.chartContainer} key={question.id}>
      <h3 className={styles.questionHeader}>{`${question.id}. ${getTranslation(question.text, lang)}`}</h3>
      <QuestionBarChart
        question={question}
        stats={questionStatistics}
      />
      {summaryLegend}
    </div>
  );
};

const SurveyQuestionSummary = ({ surveyStats, lang, extraQuestions }) => {
  const { variableStatistics, statisticsByQuestionId, survey } = surveyStats;

  const variableQuestionCodes = (variableStatistics || [])
    .reduce((codes, varStat) => codes.concat(varStat.questionCodes), []);

  const variablesDoNotContainQuestion = (question) =>
    !variableQuestionCodes.includes(question.code);

  const isNumericNonVariableQuestion = (question) =>
    isNumericQuestion(question) && variablesDoNotContainQuestion(question);

  const questionSummary = (question) =>
    statisticsByQuestionId
    && getQuestionSummary(question, statisticsByQuestionId[question.id], lang);

  const partsToShow = extraQuestions
    ? [survey.extraSurveyPart]
    : survey.surveySchemaParts.reduce((acc, part) => {
      const nonVariableSchemaPartQuestions = getSurveySchemaPartQuestions(part)
        .filter(isNumericNonVariableQuestion);
      return nonVariableSchemaPartQuestions.length ? acc.concat(part) : acc;
    }, []);

  return (
    <div className={styles.container}>
      <p>N={surveyStats.responseCount}</p>
      {partsToShow
        .map((part) => (
          <Fragment key={`ssp-${part.id}`}>
            <h2 className={styles.schemaPartTitle}>{getTranslation(part.title, lang)}</h2>
            {getSurveySchemaPartQuestions(part)
              .filter(isNumericNonVariableQuestion)
              .map(questionSummary)}
          </Fragment>
        ))}
    </div>
  );
};
const { bool } = PropTypes;
SurveyQuestionSummary.propTypes = {
  surveyStats: surveyStatsType.isRequired,
  extraQuestions: bool,
  ...langType
};

SurveyQuestionSummary.defaultProps = {
  extraQuestions: false
};

export default withLang(SurveyQuestionSummary);
